import { defineComponent, h } from 'vue';

import { Station } from '~/api/models/station.model';
import { NButton, useNotification, useLoadingBar } from 'naive-ui';
import { mapStores } from 'pinia';
import moment from 'moment';
import { useStationStore } from '~/store/station.store';
import { debounce } from 'lodash';

export type Breakpoint = 'fullhd' | 'widescreen' | 'desktop' | 'tablet' | 'mobile';

export default defineComponent({

    components: {
        NButton,
    },

    data() {

        const body: HTMLBodyElement = document.getElementsByClassName('body')[0] as HTMLBodyElement;

        return {
            body,
            width: body?.getBoundingClientRect()?.width,
            resizeHandler: debounce(this.onStationResize, 100),
            notifications: useNotification(),
            loader: useLoadingBar(),
        }
    },

    mounted() {
        this.resizeHandler();
        window.addEventListener('resize', this.resizeHandler);
    },

    unmounted() {
        window.removeEventListener('resize', this.resizeHandler);
    },

    computed: {
        ...mapStores(useStationStore),

        breakpoint(): Breakpoint {

            if(!this.width) {
                return 'desktop'
            }

            if(this.width > 1408) {
                return 'fullhd';
            } else if(this.width > 1216) {
                return 'widescreen';
            } else if (this.width > 1024) {
                return 'desktop';
            } else if (this.width > 769) {
                return 'tablet';
            } else  {
                return 'mobile';
            }
        }
    },

    methods: {

        onStationResize(): void {
            this.width = document?.documentElement?.getBoundingClientRect()?.width || 0;
        },

        getStationName(station: Station): string {
            return station?.metadata?.['elichens/public/label'] as string || station?.name || '-';
        },

        getStationPN(station: Station): string {
            return station?.metadata['elichens/public/PN'] || station?.metadata?.PN;
        },

        isLTEMStation(station: Station) {
            const pn = station?.hardware;
            if(!pn) {
                return station?.type === 'avolta-ltem';
            }

            return ['ELAV-LTEM-UL', 'ELAV-LTEM-EU'].includes(pn);
        },

        isLoRaWanStation(station: Station) {
            const pn = station?.hardware;
            if(!pn) {
                return station?.type === 'avolta-lorawan';
            }

            return ['ELAV-L868-EU', 'ELAV-L915-EU'].includes(pn);
        },

        isAvolta50(station: Station) {
            const pn = station?.hardware;
            if(!pn) {
                return station?.type === 'avolta-lorawan';
            }

            return ['ELAV50-L868-EU'].includes(pn);
        },

        isStationAllowed(stationIds: string | string[]) {
            if(Array.isArray(stationIds)) {
                return stationIds.every(id => this.stationStore.stations.map(s => s.id).includes(id))
            } else {
                return this.stationStore.stations.map(s => s.id).includes(stationIds);
            }
        },

        showLoading<T>(promise?: Promise<T>): Promise<T> | void {
            if(promise) {
                this.loader.start();
                promise
                    .catch(() => this.loader.error())
                    .finally(() => this.loader.finish())
            } else {
                this.loader.start();
            }
        },

        hideLoading(): void {
            this.loader.finish();
        },

        /**
         * Remove any forbidden station from url query
         */
        cleanQuery(): void {
            let stations: string | string[] = this.$route.query?.stations;
            if(stations && Array.isArray(stations)) {
                stations = stations.filter(s => this.isStationAllowed(s));
            } else {
                stations = this.isStationAllowed(stations) ? stations: this.stationStore.stations[0]?.id;
            }

            this.$router.replace({path: this.$route.path, query: { ...this.$route.query, station: stations}})
        },

        showUnhandledError(message: string, error?: Error) {
            this.$notifyError(message, error);
        },

        success(message: string) {
            this.notification('success', null, message);
        },

        notification(type: 'error' | 'info' | 'success' | 'warning', title?: string, message?: string, action?: { label: string, callback: () => void}) {
            const notification = this.notifications.create({
                title,
                type,
                content: () => h('span', null, [message]),
                meta: moment().format('lll'),
                duration: 5000,
                action: action ? () => {
                    return h(
                        NButton,
                        {
                            text: true,
                            type: 'primary',
                            onClick: () => {
                                action.callback();
                                notification.destroy();
                            },
                        },
                        {
                            default: () => action.label
                        }
                    )   
                } :  null,
            })
        },
    }
})